import { useState } from "react";
import { useScrollFadeIn } from "@/hooks/useScrollFadeIn";

const EMAIL = "hatomugicha91@gmail.com";

export default function ContactSection() {
  const { ref: headRef, style: headStyle } = useScrollFadeIn<HTMLDivElement>();
  const { ref: bodyRef, style: bodyStyle } = useScrollFadeIn<HTMLDivElement>({ delay: 0.15 });
  const [copied, setCopied] = useState(false);

  const handleCopyEmail = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(EMAIL);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      const el = document.createElement("textarea");
      el.value = EMAIL;
      document.body.appendChild(el);
      el.select();
      document.execCommand("copy");
      document.body.removeChild(el);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <section id="contact" className="py-14 md:py-16 bg-orange-50/70">
      <div className="max-w-3xl mx-auto px-4 md:px-10">

        {/* Heading */}
        <div ref={headRef} style={headStyle} className="text-center mb-4 md:mb-6">
          <p className="text-xs tracking-[0.4em] text-orange-600 uppercase mb-1.5 md:mb-2">Reservation</p>
          <h2 className="text-2xl md:text-3xl font-normal text-stone-800 tracking-widest">
            ご予約
          </h2>
          <div className="w-10 h-0.5 bg-orange-500 mx-auto mt-3 md:mt-3.5"></div>
        </div>

        {/* Body */}
        <div ref={bodyRef} style={bodyStyle}>

          {/* ひと言 */}
          <p className="text-center text-sm text-stone-700 tracking-wider mb-3 leading-relaxed">
            はじめての方もお気軽にご連絡ください
          </p>

          {/* 2カード縦並び（モバイル）/ 横並び（sm以上） */}
          <div className="flex flex-col sm:flex-row gap-4 mb-4">

            {/* X DM Card */}
            <div className="flex-1 flex flex-col gap-3">
              <a
                href="https://x.com/mazo_kawaii"
                target="_blank"
                rel="noopener noreferrer nofollow"
                className="flex items-center gap-3.5 px-4 py-4 bg-white border-2 border-stone-300 active:bg-stone-100 active:border-stone-500 active:scale-[0.98] transition-all duration-150 cursor-pointer group rounded-2xl"
              >
                <div className="w-[48px] h-[48px] flex items-center justify-center bg-stone-700 group-hover:bg-stone-600 active:bg-stone-600 transition-colors rounded-full flex-shrink-0">
                  <i className="ri-twitter-x-line text-white text-lg"></i>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold text-stone-800 tracking-wide">XのDMで予約</p>
                  <p className="text-xs text-stone-400 mt-0.5">@mazo_kawaii</p>
                </div>
                <div className="w-9 h-9 flex items-center justify-center rounded-full bg-stone-100 group-hover:bg-stone-700 active:bg-stone-700 transition-colors flex-shrink-0">
                  <i className="ri-arrow-right-line text-stone-500 group-hover:text-white active:text-white transition-colors text-sm"></i>
                </div>
              </a>
              {/* X 検索ヒント */}
              <div className="flex items-center gap-2 px-3 py-2.5 bg-white/80 border border-stone-200 rounded-2xl">
                <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                  <i className="ri-search-line text-stone-400 text-xs"></i>
                </div>
                <p className="text-xs text-stone-500 tracking-wide">
                  <span className="font-semibold text-stone-600">むぎ茶 @mazo_kawaii&nbsp;</span>で検索
                </p>
              </div>
            </div>

            {/* Email Card — テラコッタで目立たせる */}
            <div className="flex-1 flex flex-col gap-3">
              <a
                href={`mailto:${EMAIL}`}
                className="flex items-center gap-3.5 px-4 py-4 bg-orange-100 border-2 border-orange-500 active:bg-orange-200 active:border-orange-600 active:scale-[0.98] transition-all duration-150 cursor-pointer group rounded-2xl"
              >
                <div className="w-[48px] h-[48px] flex items-center justify-center bg-orange-600 group-hover:bg-orange-700 active:bg-orange-700 transition-colors rounded-full flex-shrink-0">
                  <i className="ri-mail-line text-white text-lg"></i>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold text-stone-800 tracking-wide">メールで予約</p>
                  <p className="text-xs text-stone-500 mt-0.5 whitespace-nowrap">{EMAIL}</p>
                </div>
                <div className="w-9 h-9 flex items-center justify-center rounded-full bg-orange-200 group-hover:bg-orange-600 active:bg-orange-600 transition-colors flex-shrink-0">
                  <i className="ri-arrow-right-line text-orange-600 group-hover:text-white active:text-white transition-colors text-sm"></i>
                </div>
              </a>
              {/* メールアドレスコピー */}
              <button
                type="button"
                onClick={handleCopyEmail}
                className="flex items-center gap-2 px-3 py-2 bg-stone-100 border border-stone-300 active:bg-stone-200 active:scale-[0.98] transition-all duration-150 rounded-xl cursor-pointer w-full"
              >
                <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                  {copied
                    ? <i className="ri-check-line text-stone-500 text-xs"></i>
                    : <i className="ri-file-copy-line text-stone-500 text-xs"></i>
                  }
                </div>
                <p className="text-xs text-stone-600 leading-snug text-left flex-1 min-w-0 break-all">
                  {copied
                    ? <span className="font-semibold">コピーしました！</span>
                    : <>開けない時はこちらで<span className="font-semibold"> {EMAIL} </span>コピーしてメール送信してください</>
                  }
                </p>
              </button>
            </div>
          </div>

          {/* 共通：記入項目 */}
          <div className="bg-white border border-stone-200 px-4 py-4 mb-4 rounded-xl">
            <p className="text-sm text-stone-600 mb-3 tracking-wider">ご連絡の際に以下をお知らせください</p>
            <div className="flex flex-col gap-3">
              <div className="flex items-center gap-3">
                <div className="w-6 h-6 flex items-center justify-center bg-orange-100 rounded-full flex-shrink-0">
                  <i className="ri-user-line text-orange-600 text-xs"></i>
                </div>
                <span className="text-sm text-stone-600">お名前</span>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 flex items-center justify-center bg-orange-100 rounded-full flex-shrink-0 mt-0.5">
                  <i className="ri-calendar-line text-orange-600 text-xs"></i>
                </div>
                <div>
                  <span className="text-sm text-stone-600">希望日時</span>
                  <span className="text-xs text-stone-400 ml-2">（例：6月15日 14:00〜）</span>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 flex items-center justify-center bg-orange-100 rounded-full flex-shrink-0 mt-0.5">
                  <i className="ri-time-line text-orange-600 text-xs"></i>
                </div>
                <div>
                  <span className="text-sm text-stone-600">コース時間</span>
                  <span className="text-xs text-stone-400 ml-2">（例：90分）</span>
                </div>
              </div>
            </div>
          </div>

          {/* 注意事項 */}
          <div className="border border-orange-200 bg-orange-50 overflow-hidden rounded-xl">
            <div className="flex items-center gap-2 px-4 py-2.5 bg-orange-100 border-b border-orange-200">
              <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                <i className="ri-information-line text-orange-500 text-sm"></i>
              </div>
              <p className="text-sm tracking-[0.1em] text-stone-600 font-medium">ご予約前にお読みください</p>
            </div>
            <div className="divide-y divide-orange-200">
              <div className="flex items-start gap-3 px-4 py-3">
                <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <i className="ri-mail-send-line text-orange-500 text-xs"></i>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-stone-600 leading-[1.7]">
                    <span className="text-sm font-semibold text-stone-600">返信メールについて</span><span className="text-stone-700 font-medium">hatomugicha91@gmail.com</span> よりご返信します。迷惑メールフォルダもご確認ください。
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3 px-4 py-3">
                <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <i className="ri-error-warning-line text-orange-500 text-xs"></i>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-stone-600 mb-1">キャンセルについて</p>
                  <p className="text-xs text-stone-600 leading-[1.7]">
                    無断当日キャンセルは<span className="text-stone-700 font-medium">プレイ料金の全額を請求</span>いたします。お支払い未確認の場合、以降のご予約はお受けできません。
                  </p>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>
    </section>
  );
}